import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { validateEmail } from '../util';
import { useTranslations } from './useTranslations';
import {
  resetLoginFailMessage,
  resetVerification,
  sendVerificationCode,
  verificationCodeFailed,
  verifyCode,
} from '../actions';

export const useOtp = (
  email,
  phoneNumber,
  onSuccessCallback = () => { },
  onVerificationTokenRetrieved = () => { },
) => {
  const isExistingUser =
    useSelector(({ quoting }) => quoting.customerValidationResponse.id);

  const verificationState = useSelector(
    ({ quoting }) => quoting.verificationCode,
    shallowEqual,
  );

  const { t } = useTranslations('quoting');
  const dispatch = useDispatch();
  const [otpCode, setOtpCode] = useState('');
  const [otpSuccess, setOtpSuccess] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [isResending, setIsResending] = useState(false);

  // existing users allways use email.
  // New users not implemented but default as SMS for now
  const isSMSEnabled = useMemo(
    () => !isExistingUser,
    [isExistingUser],
  );

  const sendOtpCode = useCallback(async () => {
    if (!isSMSEnabled) {
      if (!email || !validateEmail(email)) {
        dispatch(verificationCodeFailed(t('otpFlow.invalidEmail')));
        return;
      }
    }

    setIsResending(true);

    dispatch(sendVerificationCode({ email, phoneNumber }));
    setResendTimer(90);

    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [dispatch, email, isSMSEnabled, phoneNumber, t]);

  const validateOtpCode = useCallback(async (code) => {
    dispatch(verifyCode({ code, email, phoneNumber }));
    dispatch(resetLoginFailMessage());
  }, [dispatch, email, phoneNumber]);

  const resendOtpCode = useCallback(() => {
    if (resendTimer > 0) {
      return;
    }

    sendOtpCode();
    dispatch(resetLoginFailMessage());
    dispatch(resetVerification());
    setOtpSuccess(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resendTimer, sendOtpCode]);

  const resetOtpState = useCallback(() => {
    dispatch(resetVerification());
    setOtpSuccess(false);
    setIsResending(false);
  }, [dispatch]);

  // Watch for verification state changes
  useEffect(() => {
    if (verificationState.verified) {
      setOtpSuccess(true);
      onSuccessCallback(true);
      if (verificationState.verificationToken) {
        onVerificationTokenRetrieved();
      }
    } else if (verificationState.error) {
      setOtpCode('');
      setIsResending(false);
    } else if (verificationState.success) {
      setIsResending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    verificationState.verified,
    verificationState.error,
    verificationState.success,
  ]);

  const values = useMemo(() => ({
    isResending,
    isSMSEnabled,
    otpCode,
    otpSuccess,
    resendOtpCode,
    resendTimer,
    resetOtpState,
    sendOtpCode,
    setOtpCode,
    validateOtpCode,
    verificationState,
  }), [
    isResending,
    isSMSEnabled,
    otpCode,
    otpSuccess,
    resendOtpCode,
    resendTimer,
    resetOtpState,
    sendOtpCode,
    validateOtpCode,
    verificationState,
  ]);

  return values;
};
